import React from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import TuningAdminTable, { TuningField, boolChip, safePreview } from './TuningAdminTable';
import {
  AI_MODEL_SELECT_OPTIONS,
  DEFAULT_AI_MODEL_VALUE,
  getAiModelLabel,
} from '../../../../support/aiModelOptions';

type Props = {
  title?: string;
  description?: string;
  kindFilter?: string;
  defaultKind?: string;
  allowedKinds?: string[];
  queryKeySuffix?: string;
};

const entityTypeOptions = [
  '',
  'product',
  'product_feature',
  'product_component',
  'product_use_case',
  'solution_component',
  'go_to_market',
  'security_use_case',
  'security_use_case_component',
].map((v) => ({ label: v || 'Any', value: v }));

const resourceTypeOptions = [
  '',
  'what',
  'why',
  'sell',
  'configure',
  'validate',
  'differentiate',
  'troubleshoot',
].map((v) => ({ label: v || 'Any', value: v }));

const baseKindOptions = [
  { label: 'Entity Resource Documentation', value: 'entity_resource' },
  { label: 'Course Section', value: 'course_section' },
  { label: 'Validation', value: 'validation' },
];

const GenerationProfilesPanel: React.FC<Props> = ({
                                                    title = 'Generation Profiles',
                                                    description = 'Controls model, RAG, prompt assembly, and inclusion flags for entity documentation and snackable course generation.',
                                                    kindFilter,
                                                    defaultKind,
                                                    allowedKinds,
                                                    queryKeySuffix,
                                                  }) => {
  const kindOptions = React.useMemo(() => {
    if (!allowedKinds?.length) return baseKindOptions;
    return baseKindOptions.filter((option) => allowedKinds.includes(option.value));
  }, [allowedKinds]);

  const effectiveDefaultKind =
    defaultKind ||
    kindFilter ||
    kindOptions[0]?.value ||
    'entity_resource';

  const profileFields = React.useMemo<TuningField[]>(
    () => [
      { name: 'key', label: 'Key', required: true, helperText: 'Unique stable lookup key.' },
      { name: 'name', label: 'Name', required: true },
      { name: 'description', label: 'Description', type: 'textarea', minRows: 3 },
      { name: 'kind', label: 'Kind', type: 'select', required: true, options: kindOptions },
      { name: 'entity_type', label: 'Entity Type', type: 'select', options: entityTypeOptions },
      { name: 'resource_type', label: 'Resource Type', type: 'select', options: resourceTypeOptions },
      { name: 'section_key', label: 'Section Key', type: 'select', options: resourceTypeOptions },
      {
        name: 'model_name',
        label: 'Model',
        type: 'select',
        required: true,
        options: AI_MODEL_SELECT_OPTIONS,
        helperText: `Default: ${DEFAULT_AI_MODEL_VALUE}`,
      },
      { name: 'limiter_key', label: 'Limiter Key', required: true },
      { name: 'temperature', label: 'Temperature', type: 'number' },
      { name: 'max_output_tokens', label: 'Max Output Tokens', type: 'number' },
      { name: 'include_system_prompt', label: 'Include System Prompt', type: 'boolean' },
      { name: 'include_rag_context', label: 'Include RAG Context', type: 'boolean' },
      { name: 'rag_provider', label: 'RAG Provider' },
      { name: 'rag_max_chars', label: 'RAG Max Chars', type: 'number' },
      {
        name: 'rag_query_template',
        label: 'RAG Query Template',
        type: 'textarea',
        minRows: 3,
        helperText: 'Supports {context_name}, {entity_name}, {entity_description}, {entity_type}.',
      },
      { name: 'include_entity_scope', label: 'Include Entity Scope', type: 'boolean' },
      { name: 'include_resource_boundaries', label: 'Include Resource Boundaries', type: 'boolean' },
      { name: 'include_section_instructions', label: 'Include Section Instructions', type: 'boolean' },
      { name: 'include_depth_instructions', label: 'Include Depth Instructions', type: 'boolean' },
      { name: 'include_product_reference', label: 'Include Product Reference', type: 'boolean' },
      { name: 'include_high_level_entity_guidance', label: 'Include High-Level Guidance', type: 'boolean' },
      { name: 'include_authoritative_resource_usage', label: 'Include Authoritative Usage', type: 'boolean' },
      { name: 'minimum_word_count', label: 'Minimum Word Count', type: 'number' },
      { name: 'system_instruction', label: 'System Instruction', type: 'textarea', minRows: 4 },
      { name: 'critical_requirements', label: 'Critical Requirements', type: 'textarea', minRows: 8 },
      { name: 'additional_requirements', label: 'Additional Requirements', type: 'textarea', minRows: 6 },
      { name: 'is_active', label: 'Active', type: 'boolean' },
      { name: 'created_by', label: 'Created By' },
      { name: 'updated_by', label: 'Updated By' },
    ],
    [kindOptions],
  );

  return (
    <TuningAdminTable
      title={title}
      description={description}
      endpoint="db/ai-generation-profile/"
      queryKey={['ai-generation-profile', queryKeySuffix || kindFilter || 'all']}
      fields={profileFields}
      extraParams={kindFilter ? { kind: kindFilter } : {}}
      defaultValues={{
        key: '',
        name: '',
        description: '',
        kind: effectiveDefaultKind,
        entity_type: '',
        resource_type: '',
        section_key: '',
        model_name: DEFAULT_AI_MODEL_VALUE,
        limiter_key: effectiveDefaultKind === 'course_section' ? 'snackable' : 'chat',
        temperature: 0.2,
        max_output_tokens: null,
        include_system_prompt: true,
        include_rag_context: true,
        rag_provider: 'drift',
        rag_max_chars: 15000,
        rag_query_template:
          '{context_name} configuration setup troubleshooting competitive advantages. {entity_description}',
        include_entity_scope: true,
        include_resource_boundaries: true,
        include_section_instructions: effectiveDefaultKind === 'course_section',
        include_depth_instructions: effectiveDefaultKind === 'course_section',
        include_product_reference: effectiveDefaultKind === 'course_section',
        include_high_level_entity_guidance: effectiveDefaultKind === 'course_section',
        include_authoritative_resource_usage: true,
        minimum_word_count: 300,
        system_instruction: '',
        critical_requirements: '',
        additional_requirements: '',
        is_active: true,
        created_by: 'admin-ui',
        updated_by: 'admin-ui',
      }}
      orderBy="kind"
      rowTitle={(row) => row.name || row.key}
      rowSubtitle={(row) => safePreview(row.description, 180)}
      columns={[
        {
          key: 'name',
          label: 'Profile',
          render: (row) => (
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={700}>
                {row.name || row.key}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {row.key}
              </Typography>

              {row.description ? (
                <Typography variant="caption" color="text.secondary">
                  {safePreview(row.description, 120)}
                </Typography>
              ) : null}
            </Stack>
          ),
        },
        {
          key: 'kind',
          label: 'Kind',
          width: 170,
          render: (row) => <Chip size="small" variant="outlined" label={row.kind || '-'} />,
        },
        {
          key: 'scope',
          label: 'Scope',
          width: 260,
          sortable: false,
          render: (row) => (
            <Stack spacing={0.25}>
              <Typography variant="caption">
                Entity: <b>{row.entity_type || 'any'}</b>
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Resource: {row.resource_type || 'any'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Section: {row.section_key || 'any'}
              </Typography>
            </Stack>
          ),
        },
        {
          key: 'model',
          label: 'Model',
          width: 220,
          sortable: false,
          render: (row) => (
            <Stack spacing={0.25}>
              <Typography variant="caption">
                {getAiModelLabel(row.model_name)}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Temp: {row.temperature ?? '-'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Limiter: {row.limiter_key || '-'}
              </Typography>
            </Stack>
          ),
        },
        {
          key: 'prompt_flags',
          label: 'Prompt Assembly',
          width: 260,
          sortable: false,
          render: (row) => (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                variant="outlined"
                color={row.include_rag_context ? 'success' : 'default'}
                label={`RAG ${row.include_rag_context ? 'on' : 'off'}`}
              />

              <Chip
                size="small"
                variant="outlined"
                color={row.include_entity_scope ? 'success' : 'default'}
                label="Scope"
              />

              <Chip
                size="small"
                variant="outlined"
                color={row.include_resource_boundaries ? 'success' : 'default'}
                label="Boundaries"
              />

              <Chip
                size="small"
                variant="outlined"
                color={row.include_section_instructions ? 'success' : 'default'}
                label="Section"
              />

              <Chip
                size="small"
                variant="outlined"
                color={row.include_authoritative_resource_usage ? 'success' : 'default'}
                label="Sources"
              />
            </Stack>
          ),
        },
        {
          key: 'active',
          label: 'Active',
          width: 100,
          render: (row) => boolChip(row.is_active),
        },
      ]}
    />
  );
};

export default GenerationProfilesPanel;